import { api } from '@ppwr/convex/_generated/api'
import type { LwCourseDetail } from '@ppwr/convex/learnworlds/types'
import { useAction } from 'convex/react'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { StatusBar, StatusBarStyle } from 'expo-status-bar'
import { useCallback, useEffect, useState } from 'react'
import { ActivityIndicator, Image, Platform, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'

import { MainHeader } from '../../components/MainHeader/MainHeader'
import { DS, type DSIcon } from '../../design-system'
import { useWebCoursePlayer } from './useWebCoursePlayer'

/**
 * Every `icon` value the school's courses actually use. LearnWorlds sends `icon` and
 * `type` from different vocabularies (`yt`/`youtube`, `video`/`ivideo`), so key off
 * `icon` — it is the one meant for display.
 *
 * These are DS icons rather than unicode glyphs: the brand font has no glyph for ▶ or
 * ☰ and renders them as tofu. Only dark/currentColor icons are used here — several DS
 * icons (Book, Stars) are hardcoded white and vanish on the white section card.
 */
const UNIT_ICON: Record<string, DSIcon> = {
  video: 'Play--black',
  yt: 'Play--black',
  ebook: 'Copy',
  quiz: 'Quotation',
  written: 'Quotation',
  exam: 'CheckThick',
  survey: 'Quotation',
  certification: 'Info'
}

export const CourseDetailScreen = () => {
  const router = useRouter()
  const { slug } = useLocalSearchParams<{ slug: string }>()
  const getCourse = useAction(api.learnworlds.actions.getCourse)

  const [course, setCourse] = useState<LwCourseDetail | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    if (!slug) return
    setError(null)
    setCourse(null)
    getCourse({ slug })
      .then(setCourse)
      .catch((e: Error) => setError(e.message))
  }, [getCourse, slug])

  useEffect(() => {
    load()
  }, [load])

  const openWebPlayer = useWebCoursePlayer(setError)

  // The LearnWorlds player does not run inside a web iframe, so web opens it in its own tab.
  const openPlayer = (unitId?: string) =>
    Platform.OS === 'web'
      ? openWebPlayer(slug, unitId)
      : router.push({
          pathname: '/courses/player',
          // The player header keeps the course name; LearnWorlds' own page titles are not exposed.
          params: { slug, ...(unitId ? { unitId } : {}), ...(course?.title ? { title: course.title } : {}) }
        })

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar animated style={'dark' as StatusBarStyle} hidden={false} />
      <MainHeader
        leftPress={() => router.back()}
        // The course name is already the page heading, and a long one doesn't fit the header.
        title="Course"
        titleStyle={{ ...DS.typography['Heading 2/Regular'], marginLeft: DS.spacing[12] }}
      />
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {course === null && error === null ? <ActivityIndicator style={styles.loader} /> : null}

        {error ? (
          <View style={styles.errorBox}>
            <DS.Text variant="Body Small/Regular" color="Utility/Error">
              {error}
            </DS.Text>
            <TouchableOpacity onPress={load}>
              <DS.Text variant="Body Small/Medium">Try again</DS.Text>
            </TouchableOpacity>
          </View>
        ) : null}

        {course ? (
          <>
            {course.image ? <Image source={{ uri: course.image }} style={styles.hero} resizeMode="cover" /> : null}

            <DS.Text variant="Heading 3/Medium">{course.title}</DS.Text>
            {course.authorName ? (
              <DS.Text variant="Caption/Regular" color="Brand/Dark Grey">
                {course.authorName}
              </DS.Text>
            ) : null}
            {course.description ? <DS.Text variant="Body Small/Regular">{course.description}</DS.Text> : null}

            <TouchableOpacity style={styles.startButton} onPress={() => openPlayer()}>
              <DS.Text variant="Body Small/Medium" color="Global/White">
                Start course
              </DS.Text>
            </TouchableOpacity>

            {course.sections.map((section) => (
              <View key={section.id} style={styles.section}>
                <DS.Text variant="Caption/Bold" uppercase>
                  {section.title}
                </DS.Text>
                {section.units.map((unit) => (
                  <TouchableOpacity key={unit.id} style={styles.unitRow} onPress={() => openPlayer(unit.id)}>
                    <DS.Icon name={UNIT_ICON[unit.icon] ?? 'Circle'} size={20} />
                    <View style={styles.unitText}>
                      <DS.Text variant="Body Small/Regular" numberOfLines={2}>
                        {unit.title}
                      </DS.Text>
                      {/* `subtitle` is a type label ("ebook") on some units and a full
                          paragraph on others, so keep it to one line. */}
                      <DS.Text variant="Caption/Regular" color="Brand/Dark Grey" numberOfLines={1}>
                        {unit.subtitle}
                      </DS.Text>
                    </View>
                  </TouchableOpacity>
                ))}
              </View>
            ))}
          </>
        ) : null}
      </ScrollView>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream']
  },
  scrollContent: {
    padding: DS.spacing[24],
    paddingBottom: DS.spacing[48],
    gap: DS.spacing[12]
  },
  loader: {
    marginTop: DS.spacing[48]
  },
  errorBox: {
    gap: DS.spacing[8]
  },
  hero: {
    width: '100%',
    height: 180,
    borderRadius: DS.spacing[8],
    backgroundColor: DS.palette['Brand/Light Grey']
  },
  startButton: {
    alignItems: 'center',
    backgroundColor: DS.palette['Brand/Maroon'],
    borderRadius: DS.spacing[32],
    paddingVertical: DS.spacing[12]
  },
  section: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: DS.spacing[8],
    gap: DS.spacing[8],
    padding: DS.spacing[16]
  },
  unitRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: DS.spacing[12]
  },
  unitText: {
    flex: 1
  }
})
